import { useState, useEffect } from "react";
import { useAuth } from "@/modules/auth";
import { useForms } from "@/modules/form/hooks/use-forms";
import { useNavigate } from "react-router";
import { useToast } from "@/hooks/use-toast";

import { FormList, FormListSkeleton, type Form } from "@/modules/form";
import Button from "@/components/ui/button";
import { DeleteConfirmationModal } from "@/components/ui/delete-confirmation-modal";
import { CreateFormModal } from "@/components/ui/create-form-modal";
import Container from "@/components/ui/container";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";

export default function FormsPage() {
	const { currentWorkspaceId } = useAuth();
	const { forms, indexForms, createForm, updateForm, deleteForm, cloneForm } = useForms();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [selectedForm, setSelectedForm] = useState<Form | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isCloning, setIsCloning] = useState(false);
	const { showToast } = useToast();

	useEffect(() => {
		(async () => {
			if (currentWorkspaceId) {
				setLoading(true);
				const result = await indexForms(currentWorkspaceId);
				setLoading(false);
				if (result?.success && result.data && result.data.length > 0) {
					const store = useOnboardingStore.getState();
					if (!store.completedSteps.includes("create_form")) {
						store.completeStep(currentWorkspaceId, "create_form");
					}
				}
			}
		})();
	}, [indexForms]);

	const handleEdit = (id: string) => {
		navigate(`/${currentWorkspaceId}/forms/${id}/edit`);
	};

	const handleDelete = async (id: string) => {
		const form = forms?.find((form) => form.id === id) || null;
		setSelectedForm(form);
		setDeleteModalOpen(true);
	};

	const handleDeleteConfirm = async (): Promise<void> => {
		if (!currentWorkspaceId) return;
		if (selectedForm === null) return;

		setIsDeleting(true);
		const result = await deleteForm(selectedForm.id, currentWorkspaceId);
		setIsDeleting(false);
		setDeleteModalOpen(false);

		if (result.success) {
			showToast({
				title: "Form deleted",
				description: "The form has been deleted successfully.",
				status: "success",
				duration: 5000,
			});
		}
	};

	const handleStatusChange = async (id: string, status: string) => {
		if (!currentWorkspaceId) return;

		const result = await updateForm(id, currentWorkspaceId, { status });

		if (result.success) {
			showToast({
				title: "Form status updated",
				description: "The form status has been updated successfully.",
				status: "success",
				duration: 5000,
			});
		} else {
			showToast({
				title: "Failed to update form status",
				description: "Something went wrong. Please try again.",
				status: "error",
				duration: 5000,
			});
		}
	};

	const handleClone = async (id: string) => {
		if (!currentWorkspaceId || isCloning) return;

		setIsCloning(true);
		const result = await cloneForm(id, currentWorkspaceId);
		setIsCloning(false);

		if (result.success && result.data) {
			showToast({
				title: "Form cloned",
				description: "The form has been cloned successfully.",
				status: "success",
				duration: 5000,
			});
			navigate(`/${currentWorkspaceId}/forms/${result.data.id}/edit`);
		} else {
			showToast({
				title: "Failed to clone form",
				description: "Something went wrong. Please try again.",
				status: "error",
				duration: 5000,
			});
		}
	};

	const handleCreateForm = () => {
		setCreateModalOpen(true);
	};

	const handleCreateFormSubmit = async (data: { title: string; preset_id?: string | null }): Promise<{ success: boolean; data?: { id: string } }> => {
		if (!currentWorkspaceId) {
			return { success: false };
		}

		const result = await createForm(currentWorkspaceId, {
			title: data.title,
			...(data.preset_id ? { preset_id: data.preset_id } : {}),
		});

		if (result.success && result.data) {
			useOnboardingStore.getState().completeStep(currentWorkspaceId, "create_form");
			navigate(`/${currentWorkspaceId}/forms/${result.data.id}/edit`);
			return { success: true, data: { id: result.data.id } };
		} else {
			showToast({
				title: "Failed to create form",
				description: "Something went wrong. Please try again.",
				status: "error",
				duration: 5000,
			});
			throw new Error("Failed to create form");
		}
	};

	const handleCreateFormSuccess = () => {
		// Form creation and navigation handled in handleCreateFormSubmit
		// This callback is called after successful creation
	};

	const hasNoForms = !loading && forms !== null && forms.length === 0;

	if (hasNoForms) {
		return (
			<div className="py-12">
				<div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
					<h2 className="text-xl font-semibold text-primary mb-2">No forms yet</h2>
					<p className="text-tertiary text-center max-w-md mb-8">Forms help you collect testimonials from your customers through customizable feedback pages.</p>

					<button
						type="button"
						onClick={handleCreateForm}
						className="flex items-center gap-3.5 px-6 py-5 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors text-left cursor-pointer"
					>
						<svg className="w-5 h-5 text-muted shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
						</svg>
						<div className="min-w-0">
							<h3 className="font-medium text-primary text-t-15">Create your first form</h3>
							<p className="text-t-13 text-tertiary">Add a form to start collecting testimonials</p>
						</div>
					</button>
				</div>

				<CreateFormModal open={createModalOpen} onOpenChange={setCreateModalOpen} onSuccess={handleCreateFormSuccess} onSubmit={handleCreateFormSubmit} />
			</div>
		);
	}

	return (
		<div className="space-y-6 py-12">
			<Container>
				<div className="flex items-center justify-between mb-6">
					<div>
						<h1 className="text-xl font-semibold text-primary">Forms</h1>
						<p className="mt-1 text-tertiary max-w-160">Create and manage feedback forms to collect testimonials from your customers.</p>
					</div>
					<div>
						<Button className="block" type="button" onClick={handleCreateForm}>
							Create Form
						</Button>
					</div>
				</div>

				{loading && <FormListSkeleton />}
				{forms && !loading && <FormList forms={forms} onDelete={handleDelete} onEdit={handleEdit} onClone={handleClone} onStatusChange={handleStatusChange} />}
			</Container>

			<DeleteConfirmationModal
				open={deleteModalOpen}
				title="Delete Form"
				description={`Are you sure you want to delete the form "${selectedForm?.title}"? This action cannot be undone.`}
				isLoading={isDeleting}
				onOpenChange={setDeleteModalOpen}
				onConfirm={handleDeleteConfirm}
			/>

			<CreateFormModal open={createModalOpen} onOpenChange={setCreateModalOpen} onSuccess={handleCreateFormSuccess} onSubmit={handleCreateFormSubmit} />
		</div>
	);
}
